// Requests waiting to be booked — the inbound half of the module.
//
// A doctor has asked for an operation and named a preferred day and time. They
// could not see the theatre diary when they asked, so their time is a wish;
// this is where somebody who CAN see it turns the wish into a booking.
//
// Accepting is one action that may also displace a scheduled case. Freeing a
// slot and dealing with the patient being moved are deliberately not two steps:
// the displaced patient may already be fasted and on the ward, and a flow that
// lets you free the slot first is a flow that lets you forget the second half.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import moment from "moment";

import {
  IPD_COLORS,
  IPD_SHAPE,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTextField,
  ipdCardSx,
} from "../../ipd/utils";
import { formatIstDateTime, formatIstTime } from "../../../utils/istDateTime";
import {
  fetchOtRequests,
  fetchSchedule,
  fetchSetup,
  scheduleOtRequest,
} from "../api/otApi";
import { OtSelect, errorMessage, report } from "./common";

/** How far the booking sits from what was asked for, in words. */
function driftLabel(minutes) {
  if (minutes === null || minutes === undefined || minutes === 0) return null;
  const abs = Math.abs(minutes);
  const span =
    abs >= 60
      ? `${Math.floor(abs / 60)}h${abs % 60 ? String(abs % 60).padStart(2, "0") : ""}`
      : `${abs} min`;
  return `${span} ${minutes > 0 ? "later" : "earlier"} than asked`;
}

/**
 * Accept a request: pick the theatre and the real time, and if that slot is
 * taken, say what happens to whoever is in it.
 *
 * The bump section only appears once a clash is actually reported, so the
 * ordinary case — a free slot — is a three-field form.
 */
function ScheduleDialog({ request, rooms, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [clash, setClash] = useState("");
  const [dayCases, setDayCases] = useState([]);
  const [bump, setBump] = useState({ ot_case_id: "", action: "RESCHEDULE", reason: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!request) return;
    // Pre-filled with what the doctor asked for. They could not see the diary,
    // so this is a starting point, not a commitment.
    const start = request.requested_datetime
      ? moment(request.requested_datetime)
      : moment().add(1, "day").hour(9).minute(0);
    setForm({
      operating_room_id: rooms[0]?.id ?? "",
      start: start.format("YYYY-MM-DDTHH:mm"),
      end: start
        .clone()
        .add(request.estimated_duration_min || 60, "minutes")
        .format("YYYY-MM-DDTHH:mm"),
      schedule_note: "",
    });
    setClash("");
    setBump({ ot_case_id: "", action: "RESCHEDULE", reason: "" });
  }, [request, rooms]);

  // What is already in THAT theatre on THAT day, so the bump picker offers
  // the cases actually in the way rather than asking for a case number from
  // memory. The schedule endpoint is the right source: it is the diary, and
  // it carries the patient on each booking. The cases list carries neither
  // a date filter nor the booking times.
  useEffect(() => {
    if (!clash || !form?.start || !form?.operating_room_id) return;
    fetchSchedule({ date: moment(form.start).format("YYYY-MM-DD") })
      .then((body) => {
        const room = (body?.rooms || []).find(
          (r) => String(r.id) === String(form.operating_room_id)
        );
        setDayCases(room?.bookings || []);
      })
      .catch(() => setDayCases([]));
  }, [clash, form?.start, form?.operating_room_id]);

  if (!request || !form) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        operating_room_id: form.operating_room_id,
        start_datetime: moment(form.start).toISOString(),
        end_datetime: moment(form.end).toISOString(),
        schedule_note: form.schedule_note || undefined,
      };
      if (bump.ot_case_id) {
        payload.bump = {
          ot_case_id: bump.ot_case_id,
          action: bump.action,
          reason: bump.reason,
          ...(bump.action === "RESCHEDULE"
            ? {
                operating_room_id: form.operating_room_id,
                start_datetime: moment(bump.start).toISOString(),
                end_datetime: moment(bump.end).toISOString(),
              }
            : {}),
        };
      }
      const res = await scheduleOtRequest(request.id, payload);
      if (res.ok) {
        report(res);
        onDone();
        return;
      }
      // A clash is not a dead end — it is the moment the bump becomes relevant,
      // so the message opens that section rather than just complaining.
      if (/already booked/i.test(res.message || "")) {
        setClash(res.message);
      } else {
        report(res);
      }
    } finally {
      setBusy(false);
    }
  };

  const bumpIncomplete =
    bump.ot_case_id &&
    (!bump.reason.trim() ||
      (bump.action === "RESCHEDULE" && (!bump.start || !bump.end)));

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Schedule {request.patient_name}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
            bgcolor: IPD_COLORS.pageBg,
            border: `1px solid ${IPD_COLORS.border}`,
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            {request.procedure_name}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
            {request.surgeon_name || "No surgeon named"} · {request.scheduling_type}
            {request.estimated_duration_min ? ` · ${request.estimated_duration_min} min` : ""}
          </Typography>
          <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
            Asked for{" "}
            <strong>
              {request.requested_datetime
                ? formatIstDateTime(request.requested_datetime)
                : request.requested_date}
            </strong>
          </Typography>
          {request.notes && (
            <Typography sx={{ fontSize: 12.5, mt: 0.5, color: IPD_COLORS.warning.text }}>
              {request.notes}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "grid", gap: 2 }}>
          <IpdFormField label="Theatre">
            <OtSelect
              value={form.operating_room_id}
              onChange={(e) => setForm((f) => ({ ...f, operating_room_id: e.target.value }))}
              options={rooms.map((r) => ({ value: r.id, label: r.name }))}
            />
          </IpdFormField>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <IpdFormField label="Starts">
              <IpdTextField
                fullWidth
                type="datetime-local"
                value={form.start}
                onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Ends">
              <IpdTextField
                fullWidth
                type="datetime-local"
                value={form.end}
                onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
              />
            </IpdFormField>
          </Box>
          <IpdFormField label="Note (optional)">
            <IpdTextField
              fullWidth
              placeholder="why it moved, if it did"
              value={form.schedule_note}
              onChange={(e) => setForm((f) => ({ ...f, schedule_note: e.target.value }))}
            />
          </IpdFormField>
        </Box>

        {clash && (
          <Box
            sx={{
              mt: 2,
              p: 1.75,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
              bgcolor: IPD_COLORS.error.bg,
              border: `1px solid ${IPD_COLORS.error.border}`,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: IPD_COLORS.error.text }}>
              {clash}
            </Typography>
            <Typography sx={{ fontSize: 12.5, mt: 0.5, mb: 1.5 }}>
              Pick a different time, or move the case that is in the way — it cannot be
              left without a booking.
            </Typography>

            <IpdFormField label="Case to move">
              <OtSelect
                placeholder="none — I will change the time instead"
                value={bump.ot_case_id}
                onChange={(e) => setBump((b) => ({ ...b, ot_case_id: e.target.value }))}
                options={dayCases.map((b) => ({
                  value: b.ot_case_id,
                  label: `${b.ot_case?.patient_name || b.ot_case?.case_number} · ${formatIstTime(
                    b.start_datetime
                  )}–${formatIstTime(b.end_datetime)} · ${b.ot_case?.lifecycle_status}`,
                }))}
              />
            </IpdFormField>

            {bump.ot_case_id && (
              <Box sx={{ mt: 1.5, display: "grid", gap: 1.5 }}>
                <IpdFormField label="What happens to them">
                  <OtSelect
                    value={bump.action}
                    onChange={(e) => setBump((b) => ({ ...b, action: e.target.value }))}
                    options={[
                      { value: "RESCHEDULE", label: "Move to another time today" },
                      { value: "POSTPONE", label: "Postpone — they will be rebooked later" },
                    ]}
                  />
                </IpdFormField>
                {bump.action === "RESCHEDULE" && (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                    <IpdFormField label="Their new start">
                      <IpdTextField
                        fullWidth
                        type="datetime-local"
                        value={bump.start || ""}
                        onChange={(e) => setBump((b) => ({ ...b, start: e.target.value }))}
                      />
                    </IpdFormField>
                    <IpdFormField label="Their new end">
                      <IpdTextField
                        fullWidth
                        type="datetime-local"
                        value={bump.end || ""}
                        onChange={(e) => setBump((b) => ({ ...b, end: e.target.value }))}
                      />
                    </IpdFormField>
                  </Box>
                )}
                <IpdFormField label="Why they are being moved">
                  <IpdTextField
                    fullWidth
                    value={bump.reason}
                    onChange={(e) => setBump((b) => ({ ...b, reason: e.target.value }))}
                  />
                </IpdFormField>
                <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                  A postponement here is recorded as an avoidable cancellation caused by
                  the theatre, so it shows in the cancellation report.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton disabled={busy || !form.operating_room_id || bumpIncomplete} onClick={submit}>
          {bump.ot_case_id ? "Move them and book" : "Book it"}
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The queue itself. Rendered on the OT dashboard above everything else, because
 * an unbooked request is the only thing on that screen nobody else is going to
 * pick up.
 */
export default function RequestQueue({ onScheduled }) {
  const [requests, setRequests] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [scheduling, setScheduling] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const body = await fetchOtRequests({ status: "PENDING" });
      setRequests(body?.requests || []);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the requests"));
    }
  }, []);

  useEffect(() => {
    load();
    fetchSetup()
      .then((s) => setRooms((s.operating_rooms || []).filter((r) => r.active !== false)))
      .catch(() => setRooms([]));
  }, [load]);

  if (error) {
    return (
      <IpdSectionCard title="Awaiting scheduling">
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.error.text }}>{error}</Typography>
      </IpdSectionCard>
    );
  }

  // Nothing waiting is the normal state and does not need a card explaining it.
  if (requests.length === 0) return null;

  return (
    <>
      <IpdSectionCard
        title={`Awaiting scheduling (${requests.length})`}
        subtitle="Asked for by a doctor. No theatre is held until you book one."
        sx={{ mb: 2.5 }}
      >
        <Box sx={{ display: "grid", gap: 1 }}>
          {requests.map((r) => (
            <Box
              key={r.id}
              sx={{
                ...ipdCardSx,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.4fr 1.2fr 1fr auto" },
                alignItems: "center",
                gap: 2,
                p: 1.75,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  {r.patient_name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  {r.health_record_id || "no MRN"}
                  {r.age != null ? ` · ${r.age}` : ""}
                  {r.gender ? ` / ${r.gender}` : ""}
                </Typography>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13 }}>{r.procedure_name}</Typography>
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  {r.surgeon_name || "no surgeon"} · {r.scheduling_type}
                </Typography>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {r.requested_datetime
                    ? formatIstDateTime(r.requested_datetime)
                    : r.requested_date}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                  asked {formatIstDateTime(r.requested_at)}
                </Typography>
              </Box>

              <Button size="small" variant="contained" onClick={() => setScheduling(r)}>
                Schedule
              </Button>
            </Box>
          ))}
        </Box>
      </IpdSectionCard>

      <ScheduleDialog
        request={scheduling}
        rooms={rooms}
        onClose={() => setScheduling(null)}
        onDone={() => {
          setScheduling(null);
          load();
          if (onScheduled) onScheduled();
        }}
      />
    </>
  );
}

export { driftLabel };
